import { createSlice } from '@reduxjs/toolkit';

import {
  type UpdatePosCredRequestInterface,
  posCreditApi
} from '@/integrations/app.api/pos-credit.api';
import { createAsyncThunk } from '@/store/create-async-thunk';

import { posCredUserAdapter } from './adapter/orderUserAdapter';
import type { InitialStateInterface } from './types/slice-types';

const initialState: InitialStateInterface = {
  posCredUser: {
    data: null,
    status: 'idle',
    error: null
  },
  setPosCredState: {
    status: 'idle',
    error: null
  },
  current: 0
};

export const getPosCredById = createAsyncThunk(
  'posCred/getPosCredById',
  async (payload: string, { rejectWithValue }) => {
    try {
      const response = await posCreditApi.getPosCredById(payload);
      const newDate = posCredUserAdapter(response);
      return newDate;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const onAcceptPosCredOffer = createAsyncThunk(
  'posCred/onAcceptPosCredOffer',
  async (
    { requestUuid, offerUuid }: { requestUuid: string; offerUuid: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await posCreditApi.acceptOffer(offerUuid);
      await dispatch(getPosCredById(requestUuid));
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const onRefundPosCredOffer = createAsyncThunk(
  'posCred/onRefundPosCredOffer',
  async (
    {
      request_uuid,
      offer_uuid
    }: {
      request_uuid: string;
      offer_uuid: string;
    },
    { dispatch }
  ) => {
    const response = await posCreditApi.refundOffer(offer_uuid);
    dispatch(getPosCredById(request_uuid));
    return response;
  }
);

export const onCancelPosCredOffer = createAsyncThunk(
  'posCred/onCancelPosCredOffer',
  async (
    { requestUuid, offerUuid }: { requestUuid: string; offerUuid: string },
    { dispatch }
  ) => {
    const response = await posCreditApi.cancelOffer(offerUuid);
    dispatch(getPosCredById(requestUuid));
    return response;
  }
);

export const setPosCredData = createAsyncThunk(
  'posCred/setPosCredData',
  async (data: UpdatePosCredRequestInterface, { dispatch }) => {
    const response = await posCreditApi.updatePosCred(data);
    dispatch(getPosCredById(data.request_uuid));
    return response;
  }
);

export const posCredApplicationSlice = createSlice({
  name: 'posCredApplication',
  initialState,
  reducers: {
    resetPosCred: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPosCredById.pending, (state) => {
        state.posCredUser.status = 'pending';
        state.posCredUser.error = null;
      })
      .addCase(getPosCredById.fulfilled, (state, action) => {
        state.posCredUser.status = 'fulfilled';
        state.posCredUser.data = action.payload || null;
      })
      .addCase(getPosCredById.rejected, (state, action) => {
        state.posCredUser.status = 'rejected';

        state.posCredUser.error = {
          message: action.error.message || 'Неизвестная ошибка',
          code: action.error.code || 'Неизвестная ошибка'
        };
      })
      .addCase(setPosCredData.pending, (state) => {
        state.setPosCredState.status = 'pending';
        state.setPosCredState.error = null;
      })
      .addCase(setPosCredData.fulfilled, (state) => {
        state.setPosCredState.status = 'fulfilled';
      })
      .addCase(setPosCredData.rejected, (state, action) => {
        state.setPosCredState.status = 'rejected';
        state.setPosCredState.error = {
          message: action.error.message || 'Неизвестная ошибка',
          code: action.error.code || 'Неизвестная ошибка'
        };
      });
  }
});

export const { resetPosCred } = posCredApplicationSlice.actions;
